import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Button, Checkbox, Icon, Label, Message } from 'semantic-ui-react';
import moment from 'moment';

import {
    ARCHIVE_PERSON_DOCUMENT,
    SET_PERSON_REGISTRATION,
} from '../../../../mutations/PERSON_REGISTRATION';
import {
    usePersonDocumentUpload,
    type PendingDocument,
} from '../../../../hooks/usePersonDocumentUpload';
import {
    PERSON_DOCUMENT_ACCEPT,
    PERSON_DOCUMENT_KIND_OPTIONS,
} from '../../../../utils/registration';

import styles from './RegistrationSections.module.css';

/**
 * The registration half of a person's profile: what they consented to, who consented on their
 * behalf, and the paperwork behind it.
 *
 * **Neither of these components checks a role.** `user.registration` arrives null and
 * `user.supportingDocuments` empty for a viewer the server will not show them to, so rendering
 * what came back *is* the gate. The same construction the galleries and streams pages use —
 * one answer rather than a client-side guess that can disagree with the server.
 *
 * This is also the "existing person" path from the brief: people already in Joyya are
 * re-registered from their profile, and land in the same row as someone created on the new form.
 */

const CONSENT_FIELDS: Array<{ key: string; label: string }> = [
    { key: 'consentDataCollection', label: 'Collection and use of their data' },
    { key: 'consentDataAggregate', label: 'Data used in aggregate' },
    { key: 'consentDataReporting', label: 'Data used in reporting' },
    { key: 'consentDataPrograms', label: 'Data used to run our programs' },
    { key: 'consentHealthSafety', label: 'Briefed on and consented to H&S risks' },
    { key: 'idSightedByCM', label: 'ID sighted by the centre manager' },
];

const GUARDIAN_FIELDS: Array<{ key: string; label: string }> = [
    { key: 'guardianConsentsToChildData', label: "Parent consents to use of the child's data" },
    { key: 'guardianIdSightedConfirmsRelationship', label: "Parent's ID sighted, confirms relationship" },
    { key: 'guardianNoIdButConfirmed', label: 'No parent ID, confirmed to the CM’s satisfaction' },
    { key: 'guardianIsMinorNoAlternative', label: 'Parent is a minor, no adult guardian available' },
];

const Tick = ({ on }: { on: boolean }) => (
    <span className={on ? styles.tickOn : styles.tickOff}>{on ? '✓' : '✗'}</span>
);

export const ConsentSectionCard = ({
    registration,
    personId,
    canEdit,
    onRefetch,
}: {
    registration: any | null;
    personId: number;
    canEdit: boolean;
    onRefetch: () => void;
}) => {
    const [setRegistration, { loading }] = useMutation(SET_PERSON_REGISTRATION);
    const [error, setError] = useState('');

    // Someone who has never been through the new process has no row. Still render the section
    // in edit mode — recording that they have been through it is the whole point of this path.
    if (registration == null && !canEdit) {
        return null;
    }

    const commit = async (field: string, next: boolean) => {
        setError('');
        try {
            await setRegistration({
                variables: {
                    personId,
                    [field]: next,
                    // Ticking anything here means this person has been taken through the new
                    // registration. The server only stamps `completedAt` if it is not already
                    // set, so re-saving later never moves the original date.
                    registrationCompleted: true,
                },
            });
            onRefetch();
        } catch (e: any) {
            setError(e?.message || 'Could not save that.');
        }
    };

    const hasGuardian = registration?.guardianName
        || GUARDIAN_FIELDS.some(({ key }) => registration?.[key]);

    return (
        <>
            {registration?.completedAt && (
                <div className={styles.completedLine}>
                    New registration completed {moment(registration.completedAt).format('MMMM Do Y')}
                </div>
            )}

            <div className={styles.consentGrid}>
                {CONSENT_FIELDS.map(({ key, label }) => (
                    <div className={styles.consentRow} key={key}>
                        {canEdit ? (
                            <Checkbox
                                label={label}
                                disabled={loading}
                                checked={!!registration?.[key]}
                                onChange={() => commit(key, !registration?.[key])}
                            />
                        ) : (
                            <>
                                <Tick on={!!registration?.[key]} />
                                <span>{label}</span>
                            </>
                        )}
                    </div>
                ))}
            </div>

            {(hasGuardian || canEdit) && (
                <>
                    <div className={styles.subTitle}>Parent or guardian</div>
                    {registration?.guardianName && (
                        <div className={styles.guardianLine}>
                            {registration.guardianName}
                            {registration.guardianRelationship && ` — ${registration.guardianRelationship}`}
                            {registration.guardianDob && ` — born ${moment(registration.guardianDob, 'YYYY-MM-DD').format('MMMM Do Y')}`}
                        </div>
                    )}
                    <div className={styles.consentGrid}>
                        {GUARDIAN_FIELDS.map(({ key, label }) => (
                            <div className={styles.consentRow} key={key}>
                                {canEdit ? (
                                    <Checkbox
                                        label={label}
                                        disabled={loading}
                                        checked={!!registration?.[key]}
                                        onChange={() => commit(key, !registration?.[key])}
                                    />
                                ) : (
                                    <>
                                        <Tick on={!!registration?.[key]} />
                                        <span>{label}</span>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                </>
            )}

            {error && <Message error visible size="tiny" content={error} />}
        </>
    );
};

const kindLabel = (kind: string) =>
    PERSON_DOCUMENT_KIND_OPTIONS.find((o) => o.value === kind)?.text ?? kind;

export const SupportingDocumentsCard = ({
    documents,
    personId,
    canEdit,
    onRefetch,
}: {
    documents: any[];
    personId: number;
    canEdit: boolean;
    onRefetch: () => void;
}) => {
    const uploadDocuments = usePersonDocumentUpload();
    const [archiveDocument] = useMutation(ARCHIVE_PERSON_DOCUMENT);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');

    const inputId = `person-document-${personId}`;

    const add = async (pending: PendingDocument[]) => {
        setUploading(true);
        setError('');
        try {
            const failed = await uploadDocuments(personId, pending);
            if (failed.length > 0) {
                setError(`These did not upload: ${failed.join(', ')}`);
            }
            onRefetch();
        } finally {
            setUploading(false);
        }
    };

    const remove = async (documentId: number, fileName: string) => {
        if (!window.confirm(`Remove "${fileName}"?`)) return;
        await archiveDocument({ variables: { documentId } });
        onRefetch();
    };

    return (
        <>
            {documents.length === 0 && (
                <div className={styles.emptyLine}>No supporting documents.</div>
            )}

            {documents.map((doc: any) => (
                <div className={styles.documentRow} key={doc.id}>
                    <Label basic size="tiny">{kindLabel(doc.kind)}</Label>
                    <a href={doc.url} target="_blank" rel="noopener noreferrer" className={styles.documentName}>
                        {doc.fileName || 'Document'}
                    </a>
                    <span className={styles.documentDate}>
                        {doc.createdAt ? moment(doc.createdAt).format('D MMM Y') : ''}
                    </span>
                    {canEdit && (
                        <Icon
                            name="times circle"
                            color="red"
                            link
                            onClick={() => remove(doc.id, doc.fileName || 'this document')}
                        />
                    )}
                </div>
            ))}

            {canEdit && (
                <div className={styles.documentAdd}>
                    <input
                        id={inputId}
                        type="file"
                        multiple
                        style={{ display: 'none' }}
                        accept={PERSON_DOCUMENT_ACCEPT}
                        onChange={(e) => {
                            const files = Array.from(e.target.files ?? []);
                            if (files.length > 0) {
                                add(files.map((file) => ({ kind: 'PERMISSION_SLIP', file })));
                            }
                            e.target.value = '';
                        }}
                    />
                    <Button
                        basic
                        compact
                        size="tiny"
                        loading={uploading}
                        disabled={uploading}
                        onClick={() => document.getElementById(inputId)?.click()}
                    >
                        <Icon name="upload" /> Add document
                    </Button>
                </div>
            )}

            {error && <Message error visible size="tiny" content={error} />}
        </>
    );
};
